import { Dispatch, ReactNode, useCallback } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { FiltersDefinition, FiltersState } from '@factorialco/f0-react'
import {
  PerformanceAccessForSelectFragment,
  PerformanceAccessSelectorDocument,
  PerformanceAccessSelectorItemsDocument,
  PerformanceAccessSelectorItemsQuery,
  PerformanceAccessSelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelectorV2 } from 'modules/core/components/EntitySelectorV2'
import { MultiSelectState } from 'modules/core/components/EntitySelectorV2/types'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

type PerformanceAccessSelectorBaseProps = {
  label?: string
  excludeIds?: number[]
  hideLabel?: boolean
  children?: ReactNode
  mandatory?: boolean
  error?: string
  asList?: boolean
}

type PerformanceAccessSelectorProps = PerformanceAccessSelectorBaseProps &
  (
    | {
        multiple: true
        value?: number[]
        onChange: (state: MultiSelectState<FiltersState<FiltersDefinition> | undefined>) => void
      }
    | {
        multiple?: false
        value?: number
        onChange: Dispatch<number | undefined>
      }
  )

export const PerformanceAccessSelector = ({
  label,
  excludeIds,
  hideLabel,
  children,
  mandatory,
  error,
  asList,
  multiple,
  value,
  onChange,
}: PerformanceAccessSelectorProps) => {
  const extractConnection = useCallback(
    (data: PerformanceAccessSelectorQuery) => data.apiCore.accessesConnection,
    []
  )

  const extractItems = useCallback(
    (data: PerformanceAccessSelectorItemsQuery) => data.apiCore.accessesConnection.nodes,
    []
  )

  const mapItemTitle = useCallback(
    (item: PerformanceAccessForSelectFragment) => `${item.firstName} ${item.lastName}`,
    []
  )

  const mapItemAvatar = useCallback((item: PerformanceAccessForSelectFragment) => {
    return {
      firstName: item.firstName,
      lastName: item.lastName,
      src: buildUrl(item.avatar),
      type: 'person' as const,
    }
  }, [])

  const mapFiltersToVariables = useCallback(() => {
    return {
      excludeIds: excludeIds ?? [],
    }
  }, [excludeIds])

  const finalLabel =
    label ?? i18n.t('performance_process.reviews.basic_information_section.owners.add_owner')

  const component = (
    <EntitySelectorV2
      document={PerformanceAccessSelectorDocument}
      itemsDocument={PerformanceAccessSelectorItemsDocument}
      extractItems={extractItems}
      extractConnection={extractConnection}
      mapItemTitle={mapItemTitle}
      mapItemAvatar={mapItemAvatar}
      mapFiltersToVariables={mapFiltersToVariables}
      options={{ disableSelectAll: true, hideLabel, asList }}
      label={finalLabel}
      mandatory={mandatory}
      error={error}
      messages={{
        placeholder: i18n.t('employees.select_employee.other'),
      }}
      {...(multiple === true
        ? {
            multiple: true,
            value: value,
            onChange: onChange,
          }
        : { value: value, onChange: onChange })}
    >
      {children}
    </EntitySelectorV2>
  )

  if (asList) {
    return (
      <Box height="s400" flexDirection="row">
        {component}
      </Box>
    )
  }

  return component
}
